'use client'

import { useState, useContext, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/shadcn/dialog'
import { Button } from '@/components/ui/shadcn/button'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { Loader2, ExternalLink, AlertCircle } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import axios from '@/lib/api/axios'

export default function SlackOAuthModal({ open, onClose, onSuccess }) {
  const authContext = useContext(AuthContext)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [oauthUrl, setOauthUrl] = useState(null)
  const [step, setStep] = useState('start') // 'start', 'authorizing', 'success'
  const [isPolling, setIsPolling] = useState(false)
  const [teamId, setTeamId] = useState(null)

  useEffect(() => {
    if (open) {
      setStep('start')
      setError(null)
      setOauthUrl(null)
      setIsPolling(false)
      setTeamId(null)
    }
  }, [open])

  const startOAuth = async () => {
    setLoading(true)
    setError(null)

    try {
      const { data } = await axios(
        `/oauth/url`,
        { method: 'GET' },
        authContext,
        true,
        'slack',
      )

      setOauthUrl(data.auth_url)
      setError(null)
      return data.auth_url
    } catch {
      const errorMsg = 'Failed to start OAuth flow'
      setError(errorMsg)
      return null
    } finally {
      setLoading(false)
    }
  }

  const pollWorkspaceCompletionStatus = async () => {
    if (!teamId) return

    try {
      const { data } = await axios(
        `/workspace/${teamId}/completion-status`,
        { method: 'GET' },
        authContext,
        true,
        'slack',
      )

      const { completed, team_name } = data

      if (completed) {
        setIsPolling(false)
        setStep('success')
        onSuccess({ team_id: teamId, team_name })
      }
    } catch {
      // Don't set error here as it might be normal for the workspace to not exist yet
    }
  }

  useEffect(() => {
    if (isPolling && teamId) {
      const interval = setInterval(pollWorkspaceCompletionStatus, 2000)
      return () => clearInterval(interval)
    }
  }, [isPolling, teamId])

  const handleOAuthFlow = async () => {
    let urlToOpen = oauthUrl

    // Generate OAuth URL if we don't have one
    if (!urlToOpen) {
      urlToOpen = await startOAuth()
    }

    if (!urlToOpen) {
      return // Error already set by startOAuth
    }

    setStep('authorizing')
    setIsPolling(true)

    // Try to open popup with better window features
    const authWindow = window.open(
      urlToOpen,
      'slack-oauth',
      'width=600,height=800,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no',
    )

    // Check if popup was blocked
    if (!authWindow || authWindow.closed) {
      setError(
        'Popup was blocked. Please allow popups for this site and try again.',
      )
      setStep('start')
      setIsPolling(false)
      return
    }

    // Focus the popup window
    try {
      authWindow.focus()
    } catch {
      // Could not focus popup window
    }

    // Listen for messages from the OAuth callback page
    const handleMessage = event => {
      // Only accept messages from our OAuth callback page
      if (event.origin !== window.location.origin) return

      if (event.data.type === 'SLACK_OAUTH_SUCCESS') {
        const { team_id } = event.data
        setTeamId(team_id)
        setIsPolling(true)
        setStep('success')

        // Close the popup
        if (authWindow) {
          authWindow.close()
        }

        // Remove the message listener
        window.removeEventListener('message', handleMessage)
      } else if (event.data.type === 'SLACK_OAUTH_ERROR') {
        setError('OAuth authorization failed')
        setStep('start')
        setIsPolling(false)

        // Close the popup
        if (authWindow) {
          authWindow.close()
        }

        // Remove the message listener
        window.removeEventListener('message', handleMessage)
      }
    }

    // Add message listener
    window.addEventListener('message', handleMessage)

    // Stop polling if the window is closed manually
    const windowCheck = setInterval(() => {
      if (authWindow && authWindow.closed) {
        clearInterval(windowCheck)
        setIsPolling(false)
        window.removeEventListener('message', handleMessage)

        // If we're still authorizing, user probably cancelled
        if (step === 'authorizing') {
          setStep('start')
          setError('Authorization was cancelled')
        }
      }
    }, 500)

    // Cleanup after 5 minutes
    setTimeout(() => {
      clearInterval(windowCheck)
      if (authWindow) authWindow.close()
      window.removeEventListener('message', handleMessage)
      if (step === 'authorizing') {
        setError('OAuth flow timed out')
        setStep('start')
        setIsPolling(false)
      }
    }, 300000)
  }

  const getStepContent = () => {
    switch (step) {
      case 'start':
        return (
          <>
            <p className="mb-4">
              Connect your Slack workspace to receive incident notifications and
              streamline team communication.
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              You&apos;ll be redirected to Slack to authorize the connection.
              Make sure you have admin permissions in your Slack workspace.
            </p>

            {error && (
              <Alert variant="destructive" className="mt-2">
                <AlertCircle />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </>
        )

      case 'authorizing':
        return (
          <div className="slack-oauth-authorizing">
            <Loader2 className="size-6 animate-spin mb-4" />
            <p>Completing authorization with Slack...</p>
            <p className="text-sm text-muted-foreground">
              Please complete the authorization in the popup window.
            </p>
          </div>
        )

      case 'success':
        return (
          <div className="slack-oauth-success">
            <h6 className="text-base font-semibold text-green-600 mb-2">
              Successfully connected to Slack!
            </h6>
            <p className="text-sm text-muted-foreground">
              You can now manage channels and receive notifications.
            </p>
          </div>
        )

      default:
        return null
    }
  }

  const getActionButtons = () => {
    switch (step) {
      case 'start':
        return (
          <>
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleOAuthFlow} disabled={loading}>
              {loading ? (
                <Loader2 className="size-4 animate-spin mr-1" />
              ) : (
                <ExternalLink className="size-4 mr-1" />
              )}
              {loading ? 'Generating...' : 'Connect via Popup'}
            </Button>
          </>
        )

      case 'authorizing':
        return (
          <Button variant="outline" onClick={onClose} disabled>
            Authorizing...
          </Button>
        )

      case 'success':
        return (
          <Button onClick={() => onSuccess({ team_id: teamId })}>Done</Button>
        )

      default:
        return null
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) onClose()
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Connect Slack Workspace</DialogTitle>
        </DialogHeader>
        {getStepContent()}
        <DialogFooter className="slack-oauth-actions">
          {getActionButtons()}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
